import { isOrderAwaitingPrepayment } from '../modules/orders/orderPaymentMode';
import type { OrderQuantityPreviewSource } from '../modules/orders/components/OrderQuantityPreview';
import { formatUnit } from '../utils/units';
import { getCustomerContactsSearchText } from '../utils/customerContacts';
import {
  getOrderProductDisplayDimensions,
  getOrderProductDisplayName,
} from '../modules/orders/orderProductDisplay';

export type OrderTableProduct = OrderQuantityPreviewSource & {
  id?: string | null;
  name?: string | null;
  productVariantId?: string | null;
  productCode?: string | null;
  productEan?: string | null;
  catalogProductName?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string>;
  variantIsDefault?: boolean;
  quantity?: number | null;
  unit?: string | null;
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
  catalogProductWidth?: number | string | null;
  catalogProductHeight?: number | string | null;
  catalogProductLength?: number | string | null;
  catalogProductThickness?: number | string | null;
  productCategoryDimensionDisplayProfile?: string | null;
};

type OrderTableRecord = {
  id?: string | null;
  orderNumber?: string | null;
  useManualNumbering?: boolean | null;
  manualOrderNumber?: string | null;
  clientOrderNumber?: string | null;
  proformaNumber?: string | null;
  producer?: string | null;
  supplierId?: string | null;
  factoryLocation?: string | null;
  deliveryLocation?: string | null;
  expectedDelivery?: string | null;
  estimatedCompletionDate?: string | null;
  orderDate?: string | null;
  status?: string | null;
  paymentMode?: string | null;
  prepaymentPaidAt?: string | null;
  customer?: {
    id?: string | null;
    name?: string | null;
    taxId?: string | null;
    contactPerson?: string | null;
    phone?: string | null;
    contactPeople?: Array<{
      name?: string | null;
      phone?: string | null;
      email?: string | null;
      isPrimary?: boolean;
    }> | null;
  } | null;
  accountManager?: {
    id?: string | null;
    name?: string | null;
  } | null;
  products?: OrderTableProduct[] | null;
};

export type OrderTableSortField =
  | 'orderNumber'
  | 'producer'
  | 'customer'
  | 'contact'
  | 'products'
  | 'date'
  | 'deliveryLocation'
  | 'clientOrderNumber'
  | 'proformaNumber'
  | 'status'
  | 'accountManager';

export type OrderTableFilters = {
  status: string[];
  customerId?: string;
  accountManagerId?: string;
  dateRange: [Date | null, Date | null] | null;
  search: string;
  orderNumber: string;
  factory: string;
  products: string;
  deliveryLocation: string;
  clientOrderNumber: string;
  proformaNumber: string;
  deadline?: 'upcoming' | 'overdue';
  /** View „Czeka na wpłatę”: prepaid ZK whose proforma is not paid yet (`isOrderAwaitingPrepayment`). */
  awaitingPrepayment?: boolean;
  sortBy: OrderTableSortField;
  sortOrder: 'asc' | 'desc';
};

const isMeaningfulValue = (value?: string | null, emptyValue = '-') =>
  Boolean(value && value.trim() && value.trim() !== emptyValue);

const normalizeValue = (value?: string | null) => value?.trim().toLocaleLowerCase('pl') || '';

const matchesTextFilter = (value: string, filter: string) => {
  const normalizedFilter = normalizeValue(filter);

  if (!normalizedFilter) {
    return true;
  }

  return normalizeValue(value).includes(normalizedFilter);
};

const compareStrings = (left: string, right: string) =>
  left.localeCompare(right, 'pl', { numeric: true, sensitivity: 'base' });

const isPresentString = (value: string | null): value is string => Boolean(value);
const POSTAL_CODE_RE = /\b\d{2}-\d{3}\b/;

const getOrderTableDisplayNumber = (order: OrderTableRecord) =>
  order.useManualNumbering && order.manualOrderNumber
    ? order.manualOrderNumber
    : order.orderNumber || '';

const getOrderNumberSortParts = (order: OrderTableRecord) => {
  const displayNumber = getOrderTableDisplayNumber(order).trim();
  const parts = displayNumber.split('/');

  if (parts.length === 3) {
    const [sequence, month, year] = parts.map((part) => part.trim());

    if (/^\d+$/.test(sequence) && /^\d+$/.test(month) && /^\d+$/.test(year)) {
      const periodValue = [
        year.padStart(4, '0'),
        month.padStart(2, '0'),
        sequence.padStart(6, '0'),
      ].join('-');

      return { isPeriodNumber: true, value: periodValue };
    }
  }

  return { isPeriodNumber: false, value: displayNumber };
};

const getOrderNumberSortValue = (order: OrderTableRecord) =>
  getOrderNumberSortParts(order).value;

const getOrderTableAddressParts = (value?: string | null) =>
  value
    ?.split(/[\n,;]+/)
    .map((part) => part.trim())
    .filter(Boolean) || [];

const getOrderTableProductParts = (
  product?: OrderTableProduct | null,
  emptyValue = '-'
) => {
  if (!product) {
    return [];
  }

  const name = getOrderProductDisplayName(product);
  const dimensions = getOrderProductDisplayDimensions(product);
  const quantityLabel =
    product.quantity != null ? `${product.quantity} ${formatUnit(product.unit)}` : '';

  return [name, dimensions, quantityLabel].filter((part, index, values): part is string => {
    if (!isMeaningfulValue(part, emptyValue)) {
      return false;
    }

    return values.indexOf(part) === index;
  }).filter(isPresentString);
};

export type OrderTableProductEntry = {
  key: string;
  name: string;
  quantityLabel: string | null;
  quantitySource: OrderQuantityPreviewSource;
  dimensionsLabel: string | null;
};

export const getOrderTableProductEntries = (
  products?: OrderTableProduct[] | null,
  emptyValue = '-'
): OrderTableProductEntry[] => {
  if (!products?.length) {
    return [];
  }

  return products.flatMap((product, index) => {
    const name = getOrderProductDisplayName(product);

    if (!isMeaningfulValue(name, emptyValue)) {
      return [];
    }

    const dimensions = getOrderProductDisplayDimensions(product);
    const quantityLabel = product.quantity != null
      ? `${product.quantity} ${formatUnit(product.unit)}`
      : null;

    return [{
      key: product.id || `${name}-${index}`,
      name,
      quantitySource: product,
      quantityLabel: isMeaningfulValue(quantityLabel, emptyValue) ? quantityLabel : null,
      dimensionsLabel: isMeaningfulValue(dimensions, emptyValue) ? dimensions : null,
    }];
  });
};

export const getOrderTableDeliveryLocationLines = (
  value?: string | null,
  emptyValue = '-'
): string[] => {
  if (!isMeaningfulValue(value, emptyValue)) {
    return [];
  }

  const parts = getOrderTableAddressParts(value);

  if (parts.length <= 1) {
    return parts;
  }

  if (parts.length === 2) {
    return parts;
  }

  if (parts.length === 3) {
    const [first, second, third] = parts;
    const secondHasPostal = POSTAL_CODE_RE.test(second);
    const thirdHasPostal = POSTAL_CODE_RE.test(third);

    if (secondHasPostal || thirdHasPostal) {
      if (thirdHasPostal) {
        return [first, `${second} ${third}`];
      }

      return [`${first} ${second}`, third];
    }

    return parts;
  }

  const [first, second, third, ...rest] = parts;
  const trailingParts = rest.length > 0 ? [rest.join(', ')] : [];

  if (POSTAL_CODE_RE.test(second)) {
    return [`${first} ${second}`, third, ...trailingParts];
  }

  if (POSTAL_CODE_RE.test(third)) {
    return [first, `${second} ${third}`, ...trailingParts];
  }

  return [first, `${second} ${third}`, ...trailingParts];
};

export const formatOrderTableDeliveryLocation = (
  value?: string | null,
  emptyValue = '-'
): string => {
  const lines = getOrderTableDeliveryLocationLines(value, emptyValue);

  return lines.length > 0 ? lines.join(' • ') : emptyValue;
};

export const formatOrderTableProduct = (
  product?: OrderTableProduct | null,
  emptyValue = '-'
): string => {
  const parts = getOrderTableProductParts(product, emptyValue);

  return parts.length > 0 ? parts.join(' • ') : emptyValue;
};

export const getOrderTableFactory = (
  order: OrderTableRecord,
  supplierNamesById: Map<string, string>,
  emptyValue = '-'
) => {
  const value =
    order.producer?.trim() ||
    (order.supplierId ? supplierNamesById.get(order.supplierId)?.trim() : '') ||
    order.factoryLocation?.trim() ||
    '';

  return value || emptyValue;
};

export const getOrderTableDateValue = (order: OrderTableRecord) =>
  order.expectedDelivery || order.orderDate || '';

export const getOrderTableProductsSearchValue = (
  products?: OrderTableProduct[] | null,
  emptyValue = '-'
) => {
  if (!products?.length) {
    return emptyValue;
  }

  const summaries = products
    .map((product) => formatOrderTableProduct(product, emptyValue))
    .filter((summary) => isMeaningfulValue(summary, emptyValue));
  const hiddenCatalogSearchTerms = products.flatMap((product) => [
    product.productCode?.trim(),
    product.productEan?.trim(),
  ]).filter((value): value is string => Boolean(value));

  const searchTerms = [...summaries, ...hiddenCatalogSearchTerms];
  return searchTerms.length > 0 ? searchTerms.join(' ') : emptyValue;
};

const getOrderSearchHaystack = (
  order: OrderTableRecord,
  supplierNamesById: Map<string, string>,
  emptyValue = '-'
) =>
  [
    getOrderTableDisplayNumber(order),
    getOrderTableFactory(order, supplierNamesById, emptyValue),
    order.customer?.name || '',
    order.customer?.taxId || '',
    getOrderTableProductsSearchValue(order.products, emptyValue),
    getOrderTableDateValue(order),
    order.deliveryLocation || '',
    order.clientOrderNumber || '',
    order.proformaNumber || '',
    order.status || '',
    order.accountManager?.name || '',
  ].join(' ');

const matchesDeadlineFilter = (order: OrderTableRecord, deadline?: OrderTableFilters['deadline']) => {
  if (!deadline) {
    return true;
  }

  if (!order.estimatedCompletionDate || ['DELIVERED', 'INVOICED', 'CANCELLED'].includes(order.status || '')) {
    return false;
  }

  const deadlineDate = new Date(order.estimatedCompletionDate);
  const today = new Date();
  const nextWeek = new Date();
  today.setHours(0, 0, 0, 0);
  nextWeek.setHours(0, 0, 0, 0);
  nextWeek.setDate(nextWeek.getDate() + 8);

  if (deadline === 'overdue') {
    return deadlineDate.getTime() < today.getTime();
  }

  return deadlineDate.getTime() >= today.getTime() && deadlineDate.getTime() < nextWeek.getTime();
};

const getOrderSortValue = (
  order: OrderTableRecord,
  field: OrderTableSortField,
  supplierNamesById: Map<string, string>,
  emptyValue = '-'
): string | number => {
  switch (field) {
    case 'orderNumber':
      return getOrderNumberSortValue(order);
    case 'producer':
      return getOrderTableFactory(order, supplierNamesById, emptyValue);
    case 'customer':
      return order.customer?.name || '';
    case 'contact':
      return getCustomerContactsSearchText(
        order.customer?.contactPeople,
        order.customer?.contactPerson,
        order.customer?.phone
      );
    case 'products':
      return getOrderTableProductsSearchValue(order.products, emptyValue);
    case 'date': {
      const value = getOrderTableDateValue(order);
      return value ? new Date(value).getTime() : 0;
    }
    case 'deliveryLocation':
      return order.deliveryLocation || '';
    case 'clientOrderNumber':
      return order.clientOrderNumber || '';
    case 'proformaNumber':
      return order.proformaNumber || '';
    case 'status':
      return order.status || '';
    case 'accountManager':
      return order.accountManager?.name || '';
    default:
      return '';
  }
};

export const filterAndSortOrders = (
  orders: OrderTableRecord[],
  filters: OrderTableFilters,
  supplierNamesById: Map<string, string>,
  emptyValue = '-'
) => {
  const filteredOrders = orders.filter((order) => {
    const effectiveDate = getOrderTableDateValue(order);
    const matchesDateRange =
      !filters.dateRange ||
      !filters.dateRange[0] ||
      !filters.dateRange[1] ||
      (effectiveDate &&
        new Date(effectiveDate).getTime() >= filters.dateRange[0].getTime() &&
        new Date(effectiveDate).getTime() <= filters.dateRange[1].getTime());

    return (
      (filters.status.length === 0 || filters.status.includes(order.status || '')) &&
      (!filters.customerId || order.customer?.id === filters.customerId) &&
      (!filters.accountManagerId || order.accountManager?.id === filters.accountManagerId) &&
      matchesDeadlineFilter(order, filters.deadline) &&
      (!filters.awaitingPrepayment || isOrderAwaitingPrepayment(order)) &&
      matchesDateRange &&
      matchesTextFilter(getOrderSearchHaystack(order, supplierNamesById, emptyValue), filters.search) &&
      matchesTextFilter(getOrderTableDisplayNumber(order), filters.orderNumber) &&
      matchesTextFilter(getOrderTableFactory(order, supplierNamesById, emptyValue), filters.factory) &&
      matchesTextFilter(getOrderTableProductsSearchValue(order.products, emptyValue), filters.products) &&
      matchesTextFilter(order.deliveryLocation || '', filters.deliveryLocation) &&
      matchesTextFilter(order.clientOrderNumber || '', filters.clientOrderNumber) &&
      matchesTextFilter(order.proformaNumber || '', filters.proformaNumber)
    );
  });

  return [...filteredOrders].sort((left, right) => {
    const direction = filters.sortOrder === 'asc' ? 1 : -1;

    if (filters.sortBy === 'orderNumber') {
      const leftNumber = getOrderNumberSortParts(left);
      const rightNumber = getOrderNumberSortParts(right);

      if (leftNumber.isPeriodNumber !== rightNumber.isPeriodNumber) {
        return leftNumber.isPeriodNumber ? -1 : 1;
      }

      return compareStrings(leftNumber.value, rightNumber.value) * direction;
    }

    const leftValue = getOrderSortValue(left, filters.sortBy, supplierNamesById, emptyValue);
    const rightValue = getOrderSortValue(right, filters.sortBy, supplierNamesById, emptyValue);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    return compareStrings(String(leftValue), String(rightValue)) * direction;
  });
};
